import { useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Image as ImageIcon, Loader2, Sparkles, Search, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminBrokenImages — scan articles whose cover_image_url is missing or
 * broken (placeholders, dead /wp-content/ paths, unreachable hosts) and
 * regenerate them via Nano Banana, with an optional preview-confirm step.
 */
export default function AdminBrokenImages() {
  const { authFetch } = useAdmin();
  const [scanning, setScanning] = useState(false);
  const [running, setRunning] = useState(false);
  const [scan, setScan] = useState(null);
  const [auto, setAuto] = useState(false);
  const [previews, setPreviews] = useState([]);

  async function doScan() {
    setScanning(true);
    setScan(null);
    setPreviews([]);
    try {
      const r = await authFetch(`${API}/admin/articles/broken-images/scan?limit=2000&sample=20`);
      if (!r.ok) throw new Error('scan failed');
      setScan(await r.json());
    } catch (e) { toast.error(e.message); }
    finally { setScanning(false); }
  }

  async function regenerate() {
    if (!window.confirm(`Pokrenuti ${auto ? 'auto-' : 'preview-'}regeneraciju za do 50 članaka? Koristi Nano Banana — može potrajati nekoliko minuta.`)) return;
    setRunning(true);
    try {
      const r = await authFetch(`${API}/admin/articles/broken-images/regenerate`, {
        method: 'POST', body: JSON.stringify({ auto, limit: 50 }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Greška');
      if (auto) {
        toast.success(`Auto-popravljeno: ${d.fixed?.length || 0}, neuspjelo ${d.failed?.length || 0}`);
        doScan();
      } else {
        setPreviews(d.previews || []);
        toast.success(`Preview spreman za ${d.previews?.length || 0} članaka.`);
      }
    } catch (e) { toast.error(e.message); }
    finally { setRunning(false); }
  }

  async function applyPreview(p) {
    const r = await authFetch(`${API}/admin/articles/broken-images/apply`, {
      method: 'POST',
      body: JSON.stringify({ article_id: p.id, cover_url: p.new_cover, method: p.method }),
    });
    if (r.ok) { toast.success('Primijenjeno'); setPreviews(arr => arr.filter(x => x.id !== p.id)); }
    else toast.error('Greška');
  }

  function rejectPreview(p) {
    setPreviews(arr => arr.filter(x => x.id !== p.id));
  }

  return (
    <div className="p-4 md:p-6" data-testid="admin-broken-images">
      <div className="flex items-center gap-3 mb-5">
        <ImageIcon className="text-pink-400" size={24} />
        <div>
          <h1 className="text-2xl font-bold">Polomljene slike — Nano Banana regeneracija</h1>
          <p className="text-sm text-zinc-400">Skenira članke s nedostajućim/polomljenim coverima i regenerira slike preko Gemini Nano Banana modela.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={doScan} disabled={scanning} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm disabled:opacity-50" data-testid="scan-btn">
          {scanning ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Skeniraj polomljene slike
        </button>
        <label className="inline-flex items-center gap-2 text-sm px-3">
          <input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)} data-testid="auto-toggle" />
          Auto-primijeni (bez previewa)
        </label>
        <button onClick={regenerate} disabled={running || !scan?.matched}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-pink-600 hover:bg-pink-700 text-white text-sm font-semibold disabled:opacity-50" data-testid="regen-btn">
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          Regeneriraj (do 50)
        </button>
      </div>

      {scan && (
        <div className="rounded-lg border border-zinc-800 p-4 mb-6 bg-zinc-950" data-testid="scan-result">
          <p className="text-sm">
            Pronađeno <strong className="text-pink-400">{scan.matched}</strong> članaka s polomljenom/nedostajućom slikom (skenirano {scan.scanned}).
          </p>
          {scan.samples?.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-zinc-400 max-h-48 overflow-auto">
              {scan.samples.map(s => (
                <li key={s.id} className="truncate" data-testid={`sample-${s.id}`}>· {s.title_hr} — {s.has_gallery ? '(galerija postoji)' : '(no gallery)'}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {previews.length > 0 && (
        <div className="space-y-3" data-testid="preview-list">
          <h2 className="text-lg font-semibold">Preview ({previews.length})</h2>
          {previews.map(p => (
            <div key={p.id} className="rounded-lg border border-zinc-800 p-4 flex gap-4" data-testid={`preview-${p.id}`}>
              <div className="grid grid-cols-2 gap-2 flex-1">
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 mb-1">Stari cover</p>
                  <img src={p.old_cover || ''} alt="" className="w-full aspect-video rounded bg-zinc-900 object-cover" />
                </div>
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 mb-1">Novi {p.method === 'gallery_promote' ? '(iz galerije)' : '(AI Nano Banana)'}</p>
                  <img src={p.new_cover} alt="" className="w-full aspect-video rounded bg-zinc-900 object-cover" />
                </div>
              </div>
              <div className="w-48 flex flex-col gap-2">
                <p className="text-sm font-semibold line-clamp-3">{p.title_hr}</p>
                <button onClick={() => applyPreview(p)} className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded" data-testid={`preview-apply-${p.id}`}>
                  <Check size={12} /> Primijeni
                </button>
                <button onClick={() => rejectPreview(p)} className="inline-flex items-center gap-1 px-3 py-1.5 bg-zinc-800 text-zinc-300 text-xs rounded">
                  <X size={12} /> Preskoči
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
